import { useNavigate, useParams } from "react-router-dom";
import BackIcon from "../../components/back-icon";
import { Button } from "../../components/ui/button";
import { format } from "date-fns";
import { CircleChevronRight } from "lucide-react";
import { usePrescriptions } from "../../lib/hooks/doctors/use-prescription";
import { base64PDFToBlobUrl } from "../../lib/utils";
import { useToast } from "../../components/ui/use-toast";
import { useAuth } from "../../contexts/auth-context";
import { IPrescriptionReport } from "../../types";
import { getReport } from "../../services/doctors";

function Prescriptions() {
  const { toast } = useToast();

  const navigate = useNavigate();
  const params = useParams();
  const professionalId = params.professionalId;

  const selectedPatient = localStorage.getItem("selectedPatient");

  const { mobile } = useAuth();

  const { prescriptions = [], isLoading } = usePrescriptions({
    professionalId,
    mobileNumber: mobile,
    patientName: selectedPatient,
  });

  async function handleViewPrescription(prescription: IPrescriptionReport) {
    try {
      // Ensure patientId and visitId exist to avoid undefined errors
      const patientId = prescription?.visitInfo?.patientId;
      const visitId = prescription?.visitInfo?.id;
  
      // if (!patientId || !visitId) {
      //   return toast({
      //     title: "Invalid prescription data",
      //     description: "Patient ID or Visit ID is missing.",
      //     variant: "destructive",
      //   });
      // }
  
      // console.log("prescription is:", prescription);
      // console.log("patientId is:", patientId);
      // console.log("visitId is:", visitId);
  
      // Convert patientId and visitId to strings
      const response = await getReport(patientId.toString(), visitId.toString());
  
      // Check if response has base64 data and status is true
      if (!response?.status || !response?.base64) {
        return toast({
          title: "No Prescription Generated",
          variant: "destructive",
        });
      }
  
      // Remove MIME type prefix if present
      const base64String = response.base64.replace(/^data:application\/pdf;base64,/, '');
  
      // Convert to Blob URL and open
      const mimeType = "application/pdf";
      const blobUrl = base64PDFToBlobUrl(mimeType, base64String);
      window.open(blobUrl);
    } catch (error) {
      console.error("Error fetching prescription:", error);
      toast({
        title: "Failed to load prescription",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  }
  

  return (
    <div className="container py-6">
      <div className="mb-4 flex items-center gap-2">
      <div className="relative group">
  <Button size="icon" variant="ghost" onClick={() => navigate(-1)}>
    <BackIcon />
  </Button>

  <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:flex items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg">
    Back
  </span>
</div>

        <h2 className="font-bold sm:text-lg">
          {prescriptions.length} Prescriptions
        </h2>
      </div>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 md:gap-8 lg:grid-cols-3 2xl:grid-cols-4">
          {prescriptions.map((prescription) => (
            <li
              key={prescription.visitInfo.id}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-md border p-4"
              onClick={() => handleViewPrescription(prescription)}
            >
              <div className="flex gap-4">
                <div className="shrink-0">
                  <PrescriptionIcon />
                </div>
                <div>
                  <h4 className="font-bold">for {selectedPatient}</h4>
                  <p className="text-sm sm:text-base">
                    Prescription{" "}
                    {format(prescription.visitInfo.visitDate, "dd MMM yyyy")}
                  </p>
                  <p className="text-sm sm:text-base">
                    Consulted with {prescription.visitInfo.consultingDoctor}
                  </p>
                </div>
              </div>
              <CircleChevronRight className="text-primary" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PrescriptionIcon() {
  return (
    <svg
      width="29"
      height="29"
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24.7702 13.654V8.50651C24.7702 3.63693 23.6343 2.4165 19.0668 2.4165H9.93185C5.36435 2.4165 4.22852 3.63693 4.22852 8.50651V22.1123C4.22852 25.3265 5.9927 26.0878 8.13145 23.7919L8.1435 23.7798C9.13434 22.7286 10.6448 22.8132 11.5027 23.9611L12.7231 25.5923"
        stroke="#292D32"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M9.66602 8.4585H19.3327"
        stroke="#292D32"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10.875 13.2915H18.125"
        stroke="#292D32"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M22.0056 17.8477L17.7281 22.1252C17.559 22.2943 17.4019 22.6085 17.3656 22.8381L17.136 24.4693C17.0515 25.0614 17.4623 25.4723 18.0544 25.3877L19.6856 25.1581C19.9152 25.1218 20.2415 24.9647 20.3985 24.7956L24.6761 20.5181C25.4131 19.781 25.7636 18.9231 24.6761 17.8356C23.6006 16.7602 22.7427 17.1106 22.0056 17.8477Z"
        stroke="#292D32"
        stroke-width="1.5"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M21.3867 18.4639C21.7492 19.7689 22.7642 20.7839 24.0692 21.1464"
        stroke="#292D32"
        stroke-width="1.5"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export default Prescriptions;